import { Component, Inject, Input, OnChanges, OnInit, Output, EventEmitter, SimpleChanges } from '@angular/core';
import { FileUpDownloadController } from '@shared/src/controllers/fileupdownload/fileupdownload.controller';
import { OpenRouteCMRController } from '@shared/src/controllers/open/openroutecmr.controller';
import { RoutePartCMRSignatureAgents, RoutePartCMRSignatureAgentsEnum } from '@shared/src/controllers/route/cmr/RoutePartCMRSignatureAgents';
import { HLong } from '@shared/src/datatypes/HLong';
import { RouteECMRActionsDto } from '@shared/src/dtos/route/cmr/RouteECMRActionsDto';
import { ActionDto } from '@shared/src/dtos/maintenance/action/actionDto';
import { ActionsStripDto } from '@shared/src/dtos/maintenance/action/ActionsStripDto';
import { AlertService, HString } from '@shared/src/public-api';
import { Observable, of } from 'rxjs';

@Component({
  selector: 'shared-route-ecmr-actions',
  templateUrl: './shared.route.ecmr.actions.component.html',
  styleUrls: ['./shared.route.ecmr.actions.component.scss'],
})
export class SharedRouteECMRActionsComponent implements OnInit, OnChanges {

  constructor(private openRouteCMRController: OpenRouteCMRController,
    @Inject('AlertService') protected alertService: AlertService) { }

  @Input() public routePartCMRId: number;
  @Input() public routeUUDQR: string;
  @Input() public phaseId: number;
  @Input() public phaseUUID: string;
  @Input() public agent: RoutePartCMRSignatureAgents = RoutePartCMRSignatureAgents.none;
  @Input() public onlyIcons: boolean = false;

  @Output() public actionExecuted = new EventEmitter<ActionDto>();

  public actions: ActionsStripDto | null = null;
  ngOnInit() {
    this.loadActions();
  }

  ngOnChanges(changes: SimpleChanges) {
    const reloadKeys = ['routePartCMRId', 'routeUUDQR', 'phaseId', 'phaseUUID', 'agent'];
    if (reloadKeys.some(key => changes[key] && !changes[key].firstChange))
      this.loadActions();
  }

  public loadActions() {
    this.getECMRActions().subscribe(data => {
      this.actions = ActionsStripDto.buildFromActionsStripDto(data ? data.actions : null);
    });
  }

  private getECMRActions(): Observable<RouteECMRActionsDto> {
    if (!HLong.isNullOrNullLong(this.routePartCMRId))
      return this.openRouteCMRController.getRoute_eCMRActions(this.routePartCMRId);
    else if (!HString.isNullOrNullString(this.routeUUDQR))
      return this.openRouteCMRController.getRoute_eCMRActionsByUUID(this.routeUUDQR);
    else if (!HLong.isNullOrNullLong(this.phaseId))
      return RoutePartCMRSignatureAgentsEnum.isValid(this.agent)
        ? this.openRouteCMRController.getRoute_eCMRActionsByPhaseIdAndAgent(this.phaseId, this.agent)
        : this.openRouteCMRController.getRoute_eCMRActionsByPhaseId(this.phaseId);
    else if (!HString.isNullOrNullString(this.phaseUUID))
      return this.openRouteCMRController.getRoute_eCMRActionsByPhaseUUID(this.phaseUUID);

    return of(null);
  }

  public clickAction(action: ActionDto) {
    switch (action.actionId) {
      case "DOWNLOADDOCUMENT":
        if (!HLong.isNullOrNullLong(this.routePartCMRId))
          return this.openRouteCMRController.getRoutePartCMRPDF(this.routePartCMRId).subscribe(d => {
            FileUpDownloadController.resultDownLoad(d);
          });
        else if (!HString.isNullOrNullString(this.routeUUDQR))
          return this.openRouteCMRController.getRoutePDFByUUID(this.routeUUDQR).subscribe(d => {
            FileUpDownloadController.resultDownLoad(d);
          });
        else if (!HLong.isNullOrNullLong(this.phaseId))
          return this.openRouteCMRController.getPhasePDF(this.phaseId).subscribe(d => {
            FileUpDownloadController.resultDownLoad(d);
          });
        else if (!HString.isNullOrNullString(this.phaseUUID))
          return this.openRouteCMRController.getPhasePDFByPhaseUUID(this.phaseUUID).subscribe(d => {
            FileUpDownloadController.resultDownLoad(d);
          });
        break;
      case "DOWNLOADDELIVERYNOTEPDF":
        if (!HLong.isNullOrNullLong(this.routePartCMRId))
          return this.openRouteCMRController.getRoutePartCMRDeliveryNotesPDF(this.routePartCMRId).subscribe(d => {
            FileUpDownloadController.resultDownLoad(d);
          });
        else if (!HString.isNullOrNullString(this.routeUUDQR))
          return this.openRouteCMRController.getRouteDeliveryNotesPDFByUUID(this.routeUUDQR).subscribe(d => {
            FileUpDownloadController.resultDownLoad(d);
          });
        else if (!HLong.isNullOrNullLong(this.phaseId))
          return this.openRouteCMRController.getPhaseDeliveryNotesPDF(this.phaseId).subscribe(d => {
            FileUpDownloadController.resultDownLoad(d);
          });
        else if (!HString.isNullOrNullString(this.phaseUUID))
          return this.openRouteCMRController.getPhaseDeliveryNotesPDFByPhaseUUID(this.phaseUUID).subscribe(d => {
            FileUpDownloadController.resultDownLoad(d);
          });
        break;
      case "AUTOSIGN":
        if (!HLong.isNullOrNullLong(this.phaseId))
          return this.openRouteCMRController.autoSignByPhaseId(this.phaseId).subscribe(d => {
            this.loadActions();
            this.actionExecuted.emit(action);
          });
        else if (!HString.isNullOrNullString(this.phaseUUID))
          return this.openRouteCMRController.autoSignByPhaseUUID(this.phaseUUID).subscribe(d => {
            this.loadActions();
            this.actionExecuted.emit(action);
          });
        break;
      case "CALCULATECMR":
        if (!HLong.isNullOrNullLong(this.phaseId))
          return this.openRouteCMRController.recalculateByPhaseId(this.phaseId).subscribe(d => {
            this.loadActions();
            this.actionExecuted.emit(action);
          });
        break;
      case "OPENLINK":
        window.open(action.additionalData);
        break;
      case "COPYLINK":
        this.alertService.copyToClipboard(action.additionalData).subscribe(e => { });
        break;
      case "SENDMSGSIGNATURE":
        if (!HLong.isNullOrNullLong(this.phaseId))
          return this.openRouteCMRController.sendMsgSignatureByPhaseId(this.phaseId).subscribe(d => { });
        break;
      case "SENDMSGDOCUMENTATION":
        if (!HLong.isNullOrNullLong(this.phaseId))
          return this.openRouteCMRController.sendMsgDocumentationByPhaseId(this.phaseId).subscribe(d => { });
        break;
      case "SENDEMAILSIGNATURE":
        if (!HLong.isNullOrNullLong(this.phaseId) && RoutePartCMRSignatureAgentsEnum.isValid(this.agent))
          return this.openRouteCMRController.sendEmailSignatureByPhaseId(this.phaseId, this.agent).subscribe(d => { });
        break;
      case "SENDEMAILDOCUMENTATION":
        if (!HLong.isNullOrNullLong(this.phaseId) && RoutePartCMRSignatureAgentsEnum.isValid(this.agent))
          return this.openRouteCMRController.sendEmailDocumentationByPhaseId(this.phaseId, this.agent).subscribe(d => { });
        break;
      case "REMOVESIGNATURE":
        if (!HLong.isNullOrNullLong(this.phaseId) && RoutePartCMRSignatureAgentsEnum.isValid(this.agent))
          return this.openRouteCMRController.removeSignatureByPhaseId(this.phaseId, this.agent).subscribe(d => {
            this.loadActions();
            this.actionExecuted.emit(action);
          });
        break;
    }
  }
}
